import { useState, useEffect, useRef, useCallback } from "react";
import Main from "../../components/layouts/Main";
import PageTitle from "../../components/utils/PageTitle";
import { useUserContext } from "../../contexts/UserContext";
import Breadcrumb from "../../components/utils/Breadcrumb";
import { catchError } from "../../services/DaoService";
import { Button, Card, Col, Collapse, Select, Spin, Empty } from "antd";
import { ThunderboltOutlined } from "@ant-design/icons";
import { PlusCircleFilled, ReloadOutlined } from "@ant-design/icons";
import DetailPreferenceToggle from "../../components/utils/DetailPreferenceToggle";
import TacheCreateForm from "../../components/tache/TacheCreateForm";
import InlineTacheCreator from '../../components/tache/InlineTacheCreator';
import TacheBacklogItem from "../../components/tache/TacheBacklogItem";
import TacheDoneSection from "../../components/tache/TacheDoneSection";
import TacheQuickView from "../../components/tache/TacheQuickView";
import AdvancedFilterPanel from "../../components/utils/AdvancedFilterPanel";
import { AffaireService } from "../../services/AffaireService";
import { PrioriteService } from "../../services/PrioriteService";
import { StatutService } from "../../services/StatutService";
import { DepartementService } from "../../services/DepartementService";
import { TacheService } from "../../services/TacheService";
import { useSelector } from "@legendapp/state/react";
import { state$ } from "../../state/globalState";
import { tachesState } from "../../state/actions/tachesState";
import { useDetailPreference } from "../../hooks/useDetailPreference";

export default function TacheListPage() {
  const [newModalVisible, setNewModalVisible] = useState(false);
  const [selectedUid, setSelectedUid] = useState(null);
  const { check } = useUserContext();
  const { mode, changeMode } = useDetailPreference();
  const taches = useSelector(() => state$.taches.list.get());
  const loading = useSelector(() => state$.taches.loading.get());
  const filters = useSelector(() => state$.taches.filters.get());
  const pagination = useSelector(() => state$.taches.pagination.get());
  const sentinelRef = useRef(null);

  const [affaires, setAffaires] = useState([]);
  const [priorites, setPriorites] = useState([]);
  const [statuts, setStatuts] = useState([]);
  const [departements, setDepartements] = useState([]);

  useEffect(() => {
    Promise.all([
      AffaireService.getInstance().all(),
      PrioriteService.getInstance().all(),
      StatutService.getInstance().all(),
      DepartementService.getInstance().all(),
    ])
      .then(([a, p, s, d]) => {
        setAffaires(a.data);
        setPriorites(p.data);
        setStatuts(s.data);
        setDepartements(d.data);
      })
      .catch(catchError);
  }, []);

  useEffect(() => {
    tachesState.refreshTaches();
  }, [filters.searchText, filters.affaire_id, filters.responsable_id, filters.priorite_id, filters.statut_id, filters.departement_id, filters.date_from, filters.date_to, filters.my_role]);

  const handleIntersect = useCallback((entries) => {
    if (entries[0].isIntersecting && pagination?.next_page_url && !loading) {
      tachesState.loadMoreTaches();
    }
  }, [pagination, loading]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(handleIntersect, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [handleIntersect]);

  const setFilter = (key, value) => {
    state$.taches.filters.set({ ...state$.taches.filters.get(), [key]: value });
  };

  const getResponsables = (currentFilters) =>
    TacheService.getInstance().getResponsables(currentFilters);

  const extraFilters = (
    <>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Dossier</div>
          <Select value={filters.affaire_id} onChange={v => setFilter('affaire_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Filtrer par dossier"
            options={affaires.map(a => ({ value: a.id, label: a.nom }))} />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Priorité</div>
          <Select value={filters.priorite_id} onChange={v => setFilter('priorite_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Priorité"
            options={priorites.map(p => ({ value: p.id, label: p.nom }))} />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Statut</div>
          <Select value={filters.statut_id} onChange={v => setFilter('statut_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Statut"
            options={statuts.map(s => ({ value: s.id, label: s.nom }))} />
        </div>
      </Col>
      <Col xs={24} sm={12} lg={8}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.4px', color: '#8C9BAB' }}>Département</div>
          <Select value={filters.departement_id} onChange={v => setFilter('departement_id', v)} allowClear showSearch optionFilterProp="label" style={{ width: '100%' }} placeholder="Département"
            options={departements.map(d => ({ value: d.id, label: `${d.nom} (${d.code})` }))} />
        </div>
      </Col>
    </>
  );

  return (
    <Main>
      <Breadcrumb parentLink="/" canSee={check("VIEW-DASHBOARD")} parentLinkTitle="Accueil" title="Tâches" />
      <PageTitle
        title="Tâches"
        subTitle={`${taches.length} tâche${taches.length !== 1 ? 's' : ''}${pagination?.total ? ` sur ${pagination.total}` : ''}`}
      >
        {check("CREATE-TACHE") && (
          <Button type="primary" icon={<PlusCircleFilled />} onClick={() => setNewModalVisible(true)}>
            Créer une tâche
          </Button>
        )}
        <Button icon={<ReloadOutlined />} onClick={() => tachesState.refreshTaches()}>
          Rafraîchir
        </Button>
      </PageTitle>

      <DetailPreferenceToggle mode={mode} onChange={changeMode} />

      <Card styles={{ body: { padding: 0 } }}>
        <AdvancedFilterPanel
          filters={filters}
          onFilterChange={setFilter}
          getResponsables={getResponsables}
          extraFilters={extraFilters}
          searchPlaceholder="Rechercher une tâche (nom, code)..."
          searchValue={filters.searchText || ''}
          onSearch={v => setFilter('searchText', v || null)}
          showRoleFilter={true}
          roleFilterValue={filters.my_role}
          onRoleFilterChange={v => setFilter('my_role', v)}
        />
        <Collapse
          defaultActiveKey={['active']}
          ghost={false}
          style={{ borderRadius: 0 }}
          items={[{
            key: 'active',
            label: (
              <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 500, fontSize: 13 }}>
                <ThunderboltOutlined style={{ color: '#3E65AC' }} />
                Ouverts
                {pagination?.total != null && (
                  <span style={{ fontSize: 12, color: '#8C9BAB', fontWeight: 400 }}>({pagination.total})</span>
                )}
              </span>
            ),
            children: (
              <Spin spinning={loading && taches.length === 0}>
                {taches.length === 0 && !loading
                  ? <div style={{ padding: '24px 0' }}><Empty description="Aucune tâche ne correspond aux filtres" /></div>
                  : (
                    <div>
                      {taches.map(tache => (
                        <div key={tache.uid} style={{ borderBottom: '1px solid var(--border-subtle, #F1F2F4)' }}>
                          <TacheBacklogItem tache={tache} showAffaire onItemClick={setSelectedUid} />
                        </div>
                      ))}
                      <div ref={sentinelRef} style={{ height: 1 }} />
                      {loading && taches.length > 0 && (
                        <div style={{ textAlign: 'center', padding: '12px 0' }}>
                          <Spin size="small" />
                        </div>
                      )}
                      {check('CREATE-TACHE') && (
                        <InlineTacheCreator
                          onCreated={() => tachesState.refreshTaches()}
                        />
                      )}
                    </div>
                  )
                }
              </Spin>
            ),
          }]}
        />
        <TacheDoneSection filters={filters} showAffaire onItemClick={setSelectedUid} />
      </Card>

      <TacheCreateForm
        visible={newModalVisible}
        onCreate={() => { tachesState.refreshTaches(); setNewModalVisible(false); }}
        onCancel={() => setNewModalVisible(false)}
      />

      <TacheQuickView
        uid={selectedUid}
        mode={mode}
        onClose={() => {
          // Si la tâche consultée a été résolue pendant qu'elle était ouverte, on ne
          // la retire du backlog qu'à la fermeture du panneau — la retirer dès le
          // onUpdate ferait disparaître le contenu du panneau lui-même, puisqu'il
          // est affiché à partir de cette même liste (state$.taches.list).
          const closedUid = selectedUid;
          setSelectedUid(null);
          const closedTache = state$.taches.list.get().find(t => t.uid === closedUid);
          if (closedTache?.statut?.resolu) {
            tachesState.removeTacheFromList(closedUid);
          }
        }}
      />
    </Main>
  );
}
